import React from "react";
import { useTranslation } from "@preppa/translation";
//SKAPA Components
import Button from "@ingka/button";
import SSRIcon from "@ingka/ssr-icon";
import Tooltip from "@ingka/tooltip";
//SKAPA Icons
import doneCheckbox from "@ingka/ssr-icon/paths/checkmark-circle";
import forwardIcon from "@ingka/ssr-icon/paths/chevron-right";
import backIcon from "@ingka/ssr-icon/paths/chevron-left";
import dishIcon from "../../../../resources/icons/DISH_ICON.png";
import ingredientIcon from "../../../../resources/icons/INGREDIENT_ICON.png";
import commentIcon from "@ingka/ssr-icon/paths/speech-bubble";
//SKAPA Styles
import "@ingka/svg-icon/dist/style.css";
import "@ingka/button/dist/style.css";
import "@ingka/text/dist/style.css";
import "@ingka/focus/dist/style.css";
import "@ingka/tooltip/dist/style.css";
// Must load after SKAPA tooltip styles so the wrap override below wins the cascade
import "../../day-plan-styles/CollapsedTaskCard.css";

import {
  PlanData,
  HourlyTaskCard,
  DailyTaskCard,
  CustomTaskCard,
  TASK_STATUS,
} from "../../../../types/types";
import {
  getDisplayConversion,
  formatVolume,
  hasProducedInput,
  hasAvailableInput,
} from "../../../../utils/planDataHelpers";

interface CollapsedTaskCardProps {
  planData: PlanData;
  currentStatus: string;
  onHourlyStatusChange?: (
    card: HourlyTaskCard,
    from: string,
    to: string,
  ) => void;
  onDailyStatusChange?: (card: DailyTaskCard, from: string, to: string) => void;
  onCustomStatusChange?: (
    card: CustomTaskCard,
    from: string,
    to: string,
  ) => void;
  hourlyCard?: HourlyTaskCard;
  dailyCard?: DailyTaskCard;
  customCard?: CustomTaskCard;
  onToggle: () => void;
  liveAvailable?: number;
  liveProduced?: number;
}

function CollapsedTaskCard({
  planData,
  currentStatus,
  onHourlyStatusChange,
  onDailyStatusChange,
  onCustomStatusChange,
  hourlyCard,
  dailyCard,
  customCard,
  onToggle,
  liveAvailable,
  liveProduced,
}: CollapsedTaskCardProps) {
  const { translate } = useTranslation();
  const isDishTask =
    planData.itemSalesPlanDtos[0]?.uomCode === null ||
    planData.itemSalesPlanDtos[0]?.uomCode === "each";
  const uomCode =
    planData.itemSalesPlanDtos[0]?.uomCode === "each"
      ? ""
      : (planData.itemSalesPlanDtos[0]?.uomCode ?? "");

  const getHourlyDisplayValue = (): number => {
    if (!hourlyCard) return 0;

    const currentTotalPlan =
      hourlyCard.itemSalesPlan.plannedVol ||
      hourlyCard.itemSalesPlan.forecastVol ||
      0;
    const available = hasAvailableInput(planData)
      ? (liveAvailable ?? (hourlyCard.itemSalesPlan.availableVol || 0))
      : 0;
    const produced = hasProducedInput(planData)
      ? (liveProduced ?? (hourlyCard.itemSalesPlan.producedVol || 0))
      : 0;

    if (currentStatus === TASK_STATUS.DONE) {
      return Math.round(produced * 10) / 10;
    }

    return Math.round((currentTotalPlan - available - produced) * 10) / 10;
  };

  const getAggregatedDisplayValue = (): number => {
    const card = dailyCard || customCard;
    if (!card) return 0;

    const currentTotalPlan = card.totalPlannedVol || card.totalForecastedVol;
    const available = hasAvailableInput(planData)
      ? (liveAvailable ?? (card.itemSalesPlan.availableVol || 0))
      : 0;
    const produced = hasProducedInput(planData)
      ? (liveProduced ?? (card.itemSalesPlan.producedVol || 0))
      : 0;
    if (currentStatus === "todo") {
      return Math.round((currentTotalPlan - available - produced) * 10) / 10;
    } else if (currentStatus === "done") {
      return Math.round(produced * 10) / 10;
    } else {
      return Math.round((currentTotalPlan - available - produced) * 10) / 10;
    }
  };

  const displayValue =
    dailyCard || customCard
      ? getAggregatedDisplayValue()
      : getHourlyDisplayValue();

  const displayConversion = getDisplayConversion(planData);
  const convertedDisplayValue = displayConversion
    ? displayValue / displayConversion.conversionFactor
    : displayValue;
  const effectiveUom = displayConversion
    ? displayConversion.displayUom
    : uomCode;

  const commercialStockValue = planData.commercialStock?.toString().trim();
  const hasCommercialStock =
    Boolean(commercialStockValue) &&
    commercialStockValue.toLowerCase() !== "null" &&
    commercialStockValue.toLowerCase() !== "undefined";
  const showCommercialStock = isDishTask && hasCommercialStock;

  const triggerStatusChange = (from: string, to: string) => {
    if (hourlyCard && onHourlyStatusChange) {
      onHourlyStatusChange(hourlyCard, from, to);
    }
    if (dailyCard && onDailyStatusChange) {
      onDailyStatusChange(dailyCard, from, to);
    }
    if (customCard && onCustomStatusChange) {
      onCustomStatusChange(customCard, from, to);
    }
  };

  const handleMoveBack = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (currentStatus === "inProgress") {
      triggerStatusChange(currentStatus, "todo");
    } else if (currentStatus === "done") {
      triggerStatusChange(currentStatus, "inProgress");
    }
  };

  const handleMarkDone = (e: React.MouseEvent) => {
    e.stopPropagation();
    triggerStatusChange(currentStatus, "done");
  };

  const handleMoveForward = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (currentStatus === "todo") {
      triggerStatusChange(currentStatus, "inProgress");
    } else if (currentStatus === "inProgress") {
      triggerStatusChange(currentStatus, "done");
    }
  };

  return (
    <div className="collapsed-card-wrapper" onClick={onToggle}>
      <div className="collapsed-card-header">
        <div className="collapsed-card-item-details-container">
          <img
            className="collapsed-card-icon"
            src={isDishTask ? dishIcon : ingredientIcon}
            alt={
              isDishTask
                ? translate("common.itemTypes.dish")
                : translate("common.itemTypes.ingredient")
            }
          />
          <span
            className="collapsed-card-name-label"
            title={planData.localName}
          >
            {planData.localName}
          </span>
        </div>
        <div className="collapsed-card-task-details-wrapper">
          {showCommercialStock && (
            <div className="commercial-stock-container">
              <span className="commercial-stock-label">
                {commercialStockValue}
              </span>
              <label className="task-details-label">
                {translate("kitchenClient.dayPlan.taskCard.commercialStock")}
              </label>
            </div>
          )}
          <div className="collapsed-card-task-details-container">
            {!(
              currentStatus === TASK_STATUS.DONE && !hasProducedInput(planData)
            ) && (
              <span className="collapsed-card-span-label">
                {formatVolume(convertedDisplayValue)} {effectiveUom}
              </span>
            )}
            <label className="task-details-label">
              {currentStatus === TASK_STATUS.DONE
                ? translate("kitchenClient.dayPlan.taskCard.done")
                : translate("kitchenClient.dayPlan.taskCard.todo")}
            </label>
          </div>
        </div>
      </div>
      <div className="collapsed-card-body">
        {/*         <div>
          <input type="checkbox" onClick={(e) => e.stopPropagation()} />
          <span onClick={(e) => e.stopPropagation()}>
            {planData.localName.length > 20
              ? `${planData.localName.slice(0, 30)}...`
              : planData.localName}
          </span>
        </div> */}
      </div>
      <div className="collapsed-card-footer">
        <div id="footer-controls-container">
          <Button
            id="move-back-button"
            ssrIcon={backIcon}
            iconOnly
            type="tertiary"
            size="xsmall"
            onClick={(e: React.MouseEvent) => {
              handleMoveBack(e);
              e.stopPropagation();
            }}
            disabled={currentStatus === "todo"}
          />
          {planData.itemSalesPlanDtos?.[0]?.comment && (
            <Tooltip
              tooltipText={
                planData.itemSalesPlanDtos[0].comment ||
                translate("kitchenClient.dayPlan.taskCard.noComment")
              }
              describedById="urgent-comment-tooltip"
              position="trailing"
              className="urgent-comment-tooltip"
            >
              <SSRIcon
                paths={commentIcon}
                className="comment-icon"
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              />
            </Tooltip>
          )}
        </div>
        <Button
          ssrIcon={doneCheckbox}
          iconOnly
          type="tertiary"
          size="xsmall"
          className="done-checkbox"
          onClick={handleMarkDone}
          disabled={currentStatus === "done"}
        />
        <div id="move-forward-button-container">
          <Button
            ssrIcon={forwardIcon}
            iconOnly
            type="tertiary"
            size="xsmall"
            onClick={handleMoveForward}
            disabled={currentStatus === "done"}
          />
        </div>
      </div>
    </div>
  );
}

export default CollapsedTaskCard;
